<script>
  import { createQuery } from '@tanstack/svelte-query';
  import cls from 'classnames';
  import {
    Badge,
    Button,
    Checkbox,
    Dropdown,
    Heading,
    Input,
    PaginationItem,
    Popover,
    Select,
    Table,
    TableBody,
    TableBodyCell,
    TableBodyRow,
    TableHead,
    TableHeadCell,
  } from 'flowbite-svelte';
  import {
    FileExportSolid,
    GearSolid,
    SearchOutline,
  } from 'flowbite-svelte-icons';
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { trackEvent } from '@button/ui/utils';
  import CopyButton from './CopyButton.svelte';
  import QRCodeButton from './QRCodeButton.svelte';
  import Truncate from './Truncate.svelte';
  import {
    FILTER_COLS_SHORTLINKS,
    QUERY_KEY_SHORTLINKS,
    ROWS_PER_PAGE_SHORTLINKS,
  } from './constants';
  import { getShortlinks } from './data/getShortlinks';
  import { queryShortlinkBulkUploads } from './data/getShortlinkBulkUploads';
  import { queryUserData } from './data/userData';
  import { getLocalStorage, getRandomArbitrary } from './utils';

  export let newlyCreatedShortlinkName = '';
  const tagColors = [
    'dark',
    'red',
    'green',
    'yellow',
    'indigo',
    'purple',
    'pink',
  ];
  function getTagColor(tag) {
    return tagColors[tag.toLowerCase().charCodeAt(0) % tagColors.length];
  }

  const headerShortlink = $_('components.shortlinks.table.head.shortlink', {
    default: 'Shortlink',
  });
  const headerName = $_('components.shortlinks.table.head.name', {
    default: 'Name',
  });
  const headerCreatedDate = $_('components.shortlinks.table.head.createdDate', {
    default: 'Created Date',
  });
  const headerStatus = $_('components.shortlinks.table.head.status', {
    default: 'Status',
  });
  const headerDestination = $_('components.shortlinks.table.head.destination', {
    default: 'Destination',
  });
  const headerTags = $_('components.shortlinks.table.head.labels', {
    default: 'Labels',
  });

  let rowsPerPage = parseInt(getLocalStorage(ROWS_PER_PAGE_SHORTLINKS, 1), 10);
  const rowsPerPageSelect = [
    { value: 50, name: '50' },
    { value: 100, name: '100' },
    { value: 150, name: '150' },
  ];
  // in case the local storage value doesn't exist in the select map
  if (!rowsPerPageSelect.map((v) => v.value).includes(rowsPerPage)) {
    rowsPerPage = rowsPerPageSelect[0].value;
    localStorage.setItem(ROWS_PER_PAGE_SHORTLINKS, rowsPerPage.toString(10));
  }

  const defaultFilterColumns = {
    shortlink: { name: headerShortlink, checked: true, disabled: true },
    name: { name: headerName, checked: true, disabled: true },
    created_date: { name: headerCreatedDate, checked: true, disabled: false },
    status: { name: headerStatus, checked: true, disabled: false },
    destination: { name: headerDestination, checked: false, disabled: false },
    tags: { name: headerTags, checked: true, disabled: false },
  };
  let filterColumns = defaultFilterColumns;

  const saveFilterColumns = () => {
    localStorage.setItem(FILTER_COLS_SHORTLINKS, JSON.stringify(filterColumns));
  };

  try {
    filterColumns = JSON.parse(
      getLocalStorage(
        FILTER_COLS_SHORTLINKS,
        JSON.stringify(defaultFilterColumns),
      ),
    );

    // validate the saved columns. If we add a column in the future, we don't want to throw an index error.
    if (
      Object.keys(defaultFilterColumns).sort() !==
      Object.keys(filterColumns).sort()
    ) {
      filterColumns = JSON.parse(JSON.stringify(defaultFilterColumns));
      saveFilterColumns();
    }
  } catch {
    // if there's a parsing error, save the default so it doesn't happen again
    saveFilterColumns();
  }

  let searchInputElement;
  let searchValue = '';
  let shortlinkBulkUploadId = '';
  $: shortlinkBulkUploads = queryShortlinkBulkUploads();
  $: userQuery = queryUserData();

  onMount(async () => {
    searchInputElement = document.getElementById('search-input');
    const params = new URLSearchParams(window.location.search);
    const scrollToTarget = params.get('scrollTo');
    if (scrollToTarget) {
      const scrollToTargetElement = document.getElementById(scrollToTarget);
      if (scrollToTargetElement) {
        scrollToTargetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
    const bulkLinkFilter = params.get('shortlinkBulkUploadId');
    if (bulkLinkFilter) {
      shortlinkBulkUploadId = parseInt(bulkLinkFilter, 10);
    }
  });

  let page = 1;
  let pageSize = rowsPerPage;
  let sortBy = 'created_at';
  let desc = true;
  $: query = createQuery({
    queryKey: [
      QUERY_KEY_SHORTLINKS,
      {
        page,
        pageSize,
        sortBy,
        desc,
        searchValue,
        shortlinkBulkUploadId,
      },
    ],
    queryFn: () =>
      getShortlinks({
        page,
        pageSize,
        sortBy,
        desc,
        searchValue,
        shortlinkBulkUploadId,
      }),
  });

  // We handle sorting on the backend via the database and API.
  const sort = {
    name: {
      desc: false,
    },
    created_at: {
      desc: true,
    },
  };

  function triggerSort(event) {
    const col = event.target.attributes.name.value;
    if (!col) {
      return;
    }
    sort[col].desc = !sort[col].desc;

    desc = sort[col].desc;
    sortBy = col;
  }

  function rowsPerPageChanged() {
    page = 1;
    pageSize = rowsPerPage;
    localStorage.setItem(ROWS_PER_PAGE_SHORTLINKS, rowsPerPage.toString(10));
  }

  function nextPage() {
    // NOOP if already on last page
    if (page >= Math.ceil(($query.data?.count ?? 0) / pageSize)) {
      return;
    }
    page += 1;
  }

  function prevPage() {
    // NOOP if already on first page
    if (page === 1) {
      return;
    }
    page = Math.max(1, page - 1);
  }

  function getStatus(state) {
    const status = {
      ready: {
        status: 'Ready',
        btnClass: 'italic bg-btnStatusReady',
        tip: $_('components.shortlinks.status.ready.tip', {
          default: 'Ready to use, no activity yet.',
        }),
        note: $_('components.shortlinks.status.ready.note', {
          default: 'Updated hourly',
        }),
      },
      active: {
        status: 'Active',
        btnClass: 'bg-btnStatusActive',
        tip: $_('components.shortlinks.status.active.tip', {
          default: 'Shortlink activity detected.',
        }),
        note: $_('components.shortlinks.status.active.note', {
          default: '',
        }),
      },
    };
    return status[state] ?? status.ready;
  }

  function handleSearch() {
    searchValue = searchInputElement.value;
    page = 1;
  }

  function handleSearchValueUpdate() {
    // If the search input is empty, we want to trigger the search
    if (searchInputElement.value === '') {
      handleSearch();
    }
  }

  function exportCsv(event) {
    trackEvent('csv-export', {
      screen_name: 'Home',
    });
    let exportUrl = '/export-csv';
    if (shortlinkBulkUploadId) {
      exportUrl += `?shortlinkBulkUploadId=${shortlinkBulkUploadId}`;
    }
    window.location = exportUrl;
    event.target.blur();
  }
</script>

<div class="mt-20 rounded-2xl bg-[#F5F5F5] px-2 pb-2">
  <Heading tag="h3" class="px-6 pt-6 text-xl"
    >{$_('home.heading', { default: 'Created Shortlinks' })}</Heading
  >
  <div
    id="shortlinks"
    class="flex flex-col justify-between p-6 lg:flex-row [&>*:not(:first-child)]:mt-6 lg:[&>*:not(:first-child)]:mt-0"
  >
    <form class="flex gap-2" on:submit|preventDefault={handleSearch}>
      <Input
        id="search-input"
        placeholder={$_('components.shortlinks.search.placeholder', {
          default: 'Search for name or tag',
        })}
        on:input={handleSearchValueUpdate}
      >
        <SearchOutline slot="left" class="h-5 w-5" />
      </Input>
      <Button size="sm" on:click={handleSearch} class="hidden lg:inline-flex"
        >{$_('components.shortlinks.search.buttonTitle', {
          default: 'Search',
        })}</Button
      >
    </form>
    <div id="editItems" class="flex items-baseline">
      {#if $userQuery.isSuccess}
        {#if $userQuery.data?.object?.isShortlinkBulkUploadEnabled}
          <div
            id="manageBulkLinks"
            class="mr-1 items-baseline justify-start lg:justify-end"
          >
            {#if $shortlinkBulkUploads.isSuccess}
              <Select
                class="w-48 max-w-48 !bg-white text-base"
                bind:value={shortlinkBulkUploadId}
                items={(() => {
                  const uploads = $shortlinkBulkUploads.data.map(
                    (bulkUpload) => ({
                      name: bulkUpload.name,
                      value: bulkUpload.id,
                    }),
                  );
                  // Add an "All" option to beginning of the dropdown list
                  uploads.unshift({ name: 'All Links', value: '' });
                  return uploads;
                })()}
                placeholder={$_('home.cta.manageBulkLink', {
                  default: 'Manage Bulk Links',
                })}
              />
            {/if}
          </div>
        {/if}
      {/if}
      <div id="editButtons">
        <Button
          size="sm"
          on:click={exportCsv}
          class="!bg-white text-base !text-gray-900 hover:!text-gray-500"
        >
          <FileExportSolid class="mr-2 h-4 w-4" />
          {$_('home.cta.export', { default: 'Export' })}
        </Button>
        <Button
          size="sm"
          class="!bg-white text-base !text-gray-900 hover:!text-gray-500"
        >
          <GearSolid class="mr-2 h-4 w-4" />
          {$_('home.cta.adjust-table', { default: 'Edit Table' })}
        </Button>
        <Dropdown class="space-y-3 p-3 text-sm">
          {#each Object.entries(filterColumns) as [key, col]}
            <li>
              <Checkbox
                on:change={() => {
                  filterColumns = { ...filterColumns, [key]: col };
                  saveFilterColumns();
                }}
                bind:disabled={col.disabled}
                bind:checked={col.checked}>{col.name}</Checkbox
              >
            </li>
          {/each}
        </Dropdown>
      </div>
    </div>
  </div>
  <Table>
    <TableHead class="bg-transparent normal-case">
      <TableHeadCell
        class="whitespace-nowrap pl-4 text-base font-medium sm:scale-100"
      >
        {headerShortlink}
      </TableHeadCell>
      <TableHeadCell
        class="whitespace-nowrap pl-4 text-base font-medium sm:scale-100"
      >
        {headerName}
        <button
          name="name"
          on:click={triggerSort}
          class="ml-1 text-lg text-gray-500 hover:text-buttonBlue"
          >&updownarrow;</button
        >
      </TableHeadCell>
      {#if filterColumns.created_date.checked}
        <TableHeadCell
          class="whitespace-nowrap pl-4 text-base font-medium sm:scale-100"
        >
          {headerCreatedDate}
          <button
            name="created_at"
            on:click={triggerSort}
            class="ml-1 text-lg text-gray-500 hover:text-buttonBlue"
            >&updownarrow;</button
          >
        </TableHeadCell>
      {/if}
      {#if filterColumns.status.checked}
        <TableHeadCell
          class=" whitespace-nowrap pl-4 text-base font-medium sm:scale-100"
        >
          {headerStatus}
        </TableHeadCell>
      {/if}
      {#if filterColumns.destination.checked}
        <TableHeadCell
          class=" whitespace-nowrap pl-4 text-base font-medium sm:scale-100"
        >
          {headerDestination}
        </TableHeadCell>
      {/if}
      {#if filterColumns.tags.checked}
        <TableHeadCell
          class=" whitespace-nowrap pl-4 text-base font-medium sm:scale-100"
        >
          {headerTags}
        </TableHeadCell>
      {/if}
    </TableHead>
    <TableBody>
      <div
        class={cls(
          'absolute z-10 h-1 w-full overflow-hidden bg-buttonBlue/25',
          $query.isLoading || $query.isRefetching ? 'block' : 'hidden',
        )}
      >
        <div
          class="h-full w-full origin-[0%_50%] animate-infiniteProgress bg-buttonBlue/50"
        />
      </div>
      {#if $query.isLoading || $query.isRefetching}
        <!-- eslint-disable-next-line no-unused-vars -->
        {#each Array(5) as _}
          <TableBodyRow class="h-[61px]">
            <TableBodyCell>
              <div
                class="h-2 animate-pulse rounded-full !bg-gray-300"
                style={`width: ${getRandomArbitrary(250, 280)}px`}
              />
            </TableBodyCell>
            <TableBodyCell>
              <div
                class={`h-2 animate-pulse rounded-full !bg-gray-300`}
                style={`width: ${getRandomArbitrary(60, 100)}px`}
              />
            </TableBodyCell>
            {#if filterColumns.created_date.checked}
              <TableBodyCell>
                <div
                  class="h-2 w-[100px] animate-pulse rounded-full !bg-gray-300"
                />
              </TableBodyCell>
            {/if}
            {#if filterColumns.status.checked}
              <TableBodyCell>
                <div
                  class="h-2 w-[120px] animate-pulse rounded-full !bg-gray-300"
                />
              </TableBodyCell>
            {/if}
            {#if filterColumns.destination.checked}
              <TableBodyCell>
                <div
                  class="h-2 w-[120px] animate-pulse rounded-full !bg-gray-300"
                />
              </TableBodyCell>
            {/if}
            {#if filterColumns.tags.checked}
              <TableBodyCell>
                <div
                  class="h-2 w-[120px] animate-pulse rounded-full !bg-gray-300"
                />
              </TableBodyCell>
            {/if}
          </TableBodyRow>
        {/each}
      {/if}
      {#if $query.data?.shortlinks.length === 0 && !$query.isLoading && !$query.isRefetching}
        <TableBodyRow class="h-[200px]">
          <div
            class="absolute h-[200px] w-full bg-white text-center font-medium leading-[200px]"
          >
            <span>
              {$_('components.shortlinks.noLinks', {
                default: 'Your shortlinks will live here',
              })}
            </span>
          </div>
        </TableBodyRow>
      {/if}
      {#if $query.data?.shortlinks.length > 0 && !$query.isRefetching && !$query.isLoading}
        {#each $query.data?.shortlinks as shortlink, i}
          <TableBodyRow
            color="custom"
            class={cls(
              'transition',
              shortlink.name === newlyCreatedShortlinkName && i === 0
                ? 'delay-50 bg-btnStatusReady/50 transition duration-500 ease-in'
                : 'bg-white transition duration-700 ease-out',
            )}
          >
            <TableBodyCell>
              <div class="flex items-center justify-between gap-x-2">
                <div class="flex justify-center gap-x-2">
                  <CopyButton shortlink={shortlink.shortlink} />
                  <QRCodeButton
                    shortlinkName={shortlink.name}
                    shortlinkId={shortlink.id}
                  />
                </div>
                <img
                  class="m-1 h-8 w-8 rounded border-[0.5px] border-gray-200 object-contain"
                  src={shortlink?.metadata?.primary_product_image_url}
                  alt={shortlink.name}
                />
                <a
                  href={shortlink.shortlink}
                  target="_blank"
                  class="hidden flex-1 lg:block">{shortlink.shortlink}</a
                >
              </div>
            </TableBodyCell>
            <TableBodyCell class="max-w-xs whitespace-normal break-words">
              <a
                href={`/links?shortlinkId=${shortlink.id}`}
                class="text-blue-500 underline hover:no-underline"
                >{shortlink.name}</a
              >
            </TableBodyCell>
            {#if filterColumns.created_date.checked}
              <TableBodyCell>
                {new Date(shortlink.created_at).toLocaleDateString()}
              </TableBodyCell>
            {/if}
            {#if filterColumns.status.checked}
              <TableBodyCell>
                <div>
                  <button
                    id={`status-${i}`}
                    class={cls(
                      'disabled',
                      'rounded-full px-4 py-1',
                      getStatus(shortlink.state).btnClass,
                    )}>{getStatus(shortlink.state).status}</button
                  >
                  <Popover
                    placement="left"
                    triggerBy={`status-${i}`}
                    trigger="hover"
                    class="font-dark w-60 text-base"
                  >
                    {getStatus(shortlink.state).tip}<br />
                    <span class="text-sm italic" style="font-weight: bold"
                      >{getStatus(shortlink.state).note}</span
                    >
                  </Popover>
                </div>
              </TableBodyCell>
            {/if}
            {#if filterColumns.destination.checked}
              <TableBodyCell>
                <div class="max-w-xl whitespace-break-spaces break-words">
                  <a
                    href={shortlink.url}
                    target="_blank"
                    class="text-blue-500 underline"
                  >
                    <Truncate
                      showTooltip={false}
                      length={100}
                      text={shortlink.url}
                    />
                  </a>
                </div>
              </TableBodyCell>
            {/if}
            {#if filterColumns.tags.checked}
              <TableBodyCell>
                <div class="flex max-w-xl flex-wrap gap-1">
                  {#each shortlink.tags as tag}
                    <Badge rounded color={getTagColor(tag)}>{tag}</Badge>
                  {/each}
                </div>
              </TableBodyCell>
            {/if}
          </TableBodyRow>
        {/each}
      {/if}
    </TableBody>
  </Table>
  <div class="flex w-full flex-col items-center justify-center py-5">
    <div class="flex items-center">
      <div class="mr-2 text-sm text-gray-700">
        {$_('components.shortlinks.rows-per-page', {
          default: 'Rows per page:',
        })}
      </div>
      <div class="mr-4">
        <Select
          placeholder=""
          size="sm"
          class="mt-2"
          items={rowsPerPageSelect}
          on:change={rowsPerPageChanged}
          bind:value={rowsPerPage}
        />
      </div>
      <!-- Help text -->
      <span class="text-sm text-gray-700 dark:text-gray-400">
        {$_('components.shortlinks.showingPage-', {
          default: 'Showing page ',
        })}
        <span class="font-semibold text-gray-900 dark:text-white">{page}</span>
        {$_('components.shortlinks.-of-', {
          default: ' of ',
        })}
        <span class="font-semibold text-gray-900 dark:text-white"
          >{Math.ceil(($query.data?.count ?? 0) / pageSize)}</span
        >
      </span>
    </div>
    {#if Math.ceil(($query.data?.count ?? 0) / pageSize) > 1}<div
        class="flex w-full items-center justify-center pt-4"
      >
        {#if page > 1}
          <PaginationItem
            normalClass="text-white bg-blue-600"
            on:click={prevPage}
          >
            {$_('components.shortlinks.previous', {
              default: 'Previous',
            })}
          </PaginationItem>
        {/if}
        {#if page < Math.ceil($query.data?.count ?? 0 / pageSize)}
          <PaginationItem
            normalClass="text-white bg-blue-600"
            on:click={nextPage}
          >
            {$_('components.shortlinks.next', {
              default: 'Next',
            })}
          </PaginationItem>
        {/if}
      </div>
    {/if}
  </div>
</div>

<style global>
  input[type='checkbox']:disabled {
    @apply bg-gray-300;
  }
</style>
